<template>
  <client-only>
    <div v-if="visible" class="cas_sso_guard">
      <div class="cas_sso_guard_card">
        <h3 class="cas_sso_guard_title">学校统一身份认证</h3>
        <p class="cas_sso_guard_desc">
          统考英语已接入学校统一身份认证。请点击下方按钮前往认证；
          若您已在其他校内系统登录，确认后将自动进入统考英语；
          若尚未登录，将进入学校统一认证登录页。
        </p>
        <el-button
          class="cas_sso_guard_btn"
          type="primary"
          :loading="redirecting"
          @click="goAuth"
        >
          确认，前往统一身份认证
        </el-button>
        <button type="button" class="cas_sso_guard_guest" @click="browseAsGuest">
          暂不登录，先浏览
        </button>
      </div>
    </div>
  </client-only>
</template>

<script setup>
import { ElMessage } from "element-plus";
import { useUserStore } from "@/stores/user";
import {
  allowCasGuestBrowse,
  clearCasGuestBrowse,
  CAS_TRANSIT_PATH,
  isCasGuestBrowse,
  isCasLoginEnabled,
  markCasTransitPending,
  shouldSkipCasGuardPath,
} from "@/utils/casSso";

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

const visible = ref(false);
const redirecting = ref(false);
let guardStarted = false;

const buildCasPath = () => {
  const redirect =
    route.fullPath &&
    route.fullPath !== "/login" &&
    route.fullPath !== CAS_TRANSIT_PATH
      ? route.fullPath
      : "/";
  return {
    path: CAS_TRANSIT_PATH,
    query: { redirect },
  };
};

const goAuth = () => {
  clearCasGuestBrowse();
  markCasTransitPending();
  redirecting.value = true;
  // 硬跳转中转页，避免先挂载登录页再遮罩
  const target = buildCasPath();
  const q = new URLSearchParams(target.query).toString();
  window.location.replace(`${target.path}${q ? `?${q}` : ""}`);
};

const browseAsGuest = () => {
  allowCasGuestBrowse();
  visible.value = false;
  redirecting.value = false;
  guardStarted = false;
  ElMessage.info("已进入浏览模式。学习相关功能仍需通过学校统一身份认证登录。");
};

const tryShowGuard = async () => {
  if (!process.client) {
    return;
  }
  if (shouldSkipCasGuardPath(route.path)) {
    return;
  }
  if (route.query.guest === "1" || route.query.guest === "true") {
    allowCasGuestBrowse();
    return;
  }
  if (isCasGuestBrowse()) {
    return;
  }

  const token = userStore.getToken && userStore.getToken();
  if (token) {
    return;
  }

  if (guardStarted || visible.value) {
    return;
  }
  guardStarted = true;

  try {
    const shop = await userStore.getShopInfo();
    if (!isCasLoginEnabled(shop?.config)) {
      guardStarted = false;
      return;
    }
  } catch (e) {
    guardStarted = false;
    return;
  }

  if (userStore.getToken && userStore.getToken()) {
    guardStarted = false;
    return;
  }
  if (isCasGuestBrowse() || shouldSkipCasGuardPath(route.path)) {
    guardStarted = false;
    return;
  }

  // 仅弹窗等待用户确认，不再自动跳转
  visible.value = true;
  redirecting.value = false;
};

onMounted(() => {
  tryShowGuard();
});

watch(
  () => route.fullPath,
  () => {
    if (!visible.value && !isCasGuestBrowse()) {
      guardStarted = false;
      tryShowGuard();
    }
  }
);
</script>

<style scoped lang="scss">
.cas_sso_guard {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}
.cas_sso_guard_card {
  width: 100%;
  max-width: 420px;
  padding: 28px 24px 22px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  text-align: center;
}
.cas_sso_guard_title {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
}
.cas_sso_guard_desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.65;
  color: #4b5563;
  text-align: left;
}
.cas_sso_guard_btn {
  width: 100%;
}
.cas_sso_guard_guest {
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  line-height: 2;
  &:hover {
    color: #374151;
  }
}
</style>
